import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Shield, Database, Zap } from 'lucide-react';

/**
 * AI Consent Dialog
 *
 * Shows on first attempt to enable AI features.
 * Explains what data is sent to third-party AI providers and GDPR/privacy implications.
 * User can Accept or Decline consent.
 *
 * Accessibility:
 * - ESC key to decline/close
 * - Focus trap within dialog
 *
 * Implementation:
 * - Uses React Portal to render at document root level
 * - Escapes navbar stacking context (z-40)
 */
const AIConsentDialog = ({ isOpen, onAccept, onDecline }) => {
  // Handle ESC key to close dialog
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onDecline();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onDecline]);

  if (!isOpen) return null;

  // Render modal using portal to escape navbar stacking context
  return createPortal(
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] p-4 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ai-consent-title"
      aria-describedby="ai-consent-description"
    >
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full mx-auto my-8 border border-gray-200 dark:border-gray-700 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
              <Shield className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h2 id="ai-consent-title" className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                Enable AI Features
              </h2>
              <p id="ai-consent-description" className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Your consent is required for AI-powered functionality
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* What AI Features Do */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2 flex items-center gap-2">
              <Zap size={16} className="text-yellow-500" />
              What AI Features Provide
            </h3>
            <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1 ml-6 list-disc">
              <li>AI-powered data quality analysis and recommendations</li>
              <li>Intelligent pipeline optimization suggestions</li>
              <li>Automated debugging and error resolution</li>
              <li>Natural language pipeline assistance (Thinking Agent)</li>
              <li>Smart SQL query optimization and BI insights</li>
            </ul>
          </div>

          {/* Data Processing Notice */}
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800 dark:text-amber-200">
                <p className="font-semibold mb-1">Third-Party Data Processing</p>
                <p>
                  AI features require a connection to a third-party AI provider (e.g., OpenAI, Anthropic, Google) configured with your own API key.
                  Pipeline metadata, schema information, and your queries will be sent to your chosen provider for processing.
                  <span className="font-semibold"> Your actual data is never sent to the AI provider.</span>
                </p>
              </div>
            </div>
          </div>

          {/* Privacy & Data Protection */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2 flex items-center gap-2">
              <Database size={16} className="text-blue-500" />
              Privacy & Data Protection
            </h3>
            <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1 ml-6 list-disc">
              <li>Only pipeline metadata and schema information is shared with your AI provider</li>
              <li>No actual data from your databases/files is transmitted</li>
              <li>You are responsible for reviewing your chosen AI provider's terms and privacy policy</li>
              <li>You can revoke consent anytime using the navbar toggle</li>
              <li>All AI features will be immediately disabled upon consent revocation</li>
              <li>Compliant with GDPR (EU) and DPDPA (India) regulations</li>
            </ul>
          </div>

          {/* Legal Notice */}
          <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/50 rounded-lg p-3">
            <p>
              <span className="font-semibold">Legal Notice:</span> By enabling AI features, you consent to the processing of
              your pipeline metadata by the third-party AI provider you configure via your own API key.
              You are responsible for ensuring your use complies with your chosen provider's terms of service and privacy policy.
              You can withdraw this consent at any time.
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-3">
          <button
            onClick={onDecline}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
          >
            Decline
          </button>
          <button
            onClick={onAccept}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 rounded-lg transition-colors flex items-center gap-2"
          >
            <Shield size={16} />
            Accept & Enable AI Features
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AIConsentDialog;
